import React, { useState } from 'react';
import { Loader2, Check, Minus, Plus, Ticket, CalendarPlus, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { submitRsvp, API } from '../lib/api';
import { TicketPicker, computeTotals } from './TicketPicker';
import { GuestFields, resizeGuests } from './GuestFields';
import { money } from '../lib/events';

const EMPTY = { full_name: '', email: '', phone: '', tickets: 1 };
const inputCls = 'w-full mt-1.5 px-4 py-3.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] transition-colors';
const calBtn = 'inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah px-5 py-3 rounded-sm border border-[#4a9cd4] text-[#4a9cd4] hover:bg-[#4a9cd4] hover:text-white transition-colors';
const labelCls = 'font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase';

function TicketStepper({ value, onChange }) {
  const btn = 'w-11 h-11 rounded-full border border-[#4a9cd4] text-[#4a9cd4] flex items-center justify-center hover:bg-[#4a9cd4] hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none';
  return (
    <div className="flex items-center gap-4 mt-2">
      <button type="button" aria-label="Fewer tickets" data-testid="rsvp-tickets-minus" className={btn} disabled={value <= 1} onClick={() => onChange(value - 1)}><Minus size={17} /></button>
      <span data-testid="rsvp-tickets-value" className="font-serif-ah text-[30px] text-[#2f2f2f] w-10 text-center">{value}</span>
      <button type="button" aria-label="More tickets" data-testid="rsvp-tickets-plus" className={btn} disabled={value >= 20} onClick={() => onChange(value + 1)}><Plus size={17} /></button>
      <span className="font-sans-ah text-[13px] text-[#888]">{value === 1 ? 'Just me' : `Me + ${value - 1} guest${value > 2 ? 's' : ''}`}</span>
    </div>
  );
}

const EMPTY_PAID = { items: {}, business_name: '', promo: null, payment_method: '' };

function PaymentNote({ done }) {
  if (!done.total) return null;
  return (
    <div className="mt-6 mx-auto max-w-[440px] bg-[#fff8e6] border border-[#f1d38a] rounded-sm p-4 font-sans-ah text-[14px] text-[#5a4a1a]" data-testid="rsvp-payment-note">
      <p>Amount due: <strong>{money(done.total)}</strong></p>
      {done.payment_method === 'online' && done.online_link && (
        <a href={done.online_link} target="_blank" rel="noopener noreferrer" data-testid="rsvp-pay-online-btn" className="btn-ah-solid inline-flex items-center gap-2 font-sans-ah text-[12px] px-5 py-3 rounded-sm mt-3"><CreditCard size={15} /> PAY NOW</a>
      )}
      {done.payment_method === 'etransfer' && <p className="mt-1">Please send an e-transfer to <strong>{done.etransfer_email}</strong> with your name and the event.</p>}
      {done.payment_method === 'door' && <p className="mt-1">Please pay at the door when you arrive.</p>}
    </div>
  );
}

export default function RsvpForm({ eventId, eventTitle, event }) {
  const paidMode = !!event?.ticket_types?.length;
  const [paid, setPaid] = useState(EMPTY_PAID);
  const [guestList, setGuestList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const qty = paidMode ? computeTotals(event, paid).qty : form.tickets;
  const guests = resizeGuests(guestList, qty - 1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in your full name, email and phone number.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error('Please enter a valid email address.'); return; }
    if (guests.some((g) => !g.name.trim())) { toast.error('Please enter a name for each guest.'); return; }
    if (guests.some((g) => g.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email.trim()))) { toast.error('Please check the guest email addresses.'); return; }
    let payload = { ...form, event_id: eventId, guests };
    if (paidMode) {
      const t = computeTotals(event, paid);
      if (!t.qty) { toast.error('Please choose at least one ticket.'); return; }
      if (t.needsBusiness && !paid.business_name.trim()) { toast.error('Please enter your member business name.'); return; }
      if (t.total > 0 && !paid.payment_method) { toast.error("Please choose how you'll pay."); return; }
      payload = { ...payload, tickets: t.qty, items: t.lines.map((l) => ({ ticket_type_id: l.id, qty: l.qty })),
        business_name: paid.business_name, promo_code: paid.promo?.code || '', payment_method: paid.payment_method };
    }
    setBusy(true);
    try {
      const r = await submitRsvp(payload);
      setDone(r);
      toast.success("You're on the list! A confirmation email is on its way.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please check your details and try again.');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="rsvp-success">
        <div className="w-20 h-20 rounded-full bg-[#eef4f8] flex items-center justify-center mx-auto mb-6"><Check size={38} className="text-[#4a9cd4]" /></div>
        <h3 className="font-serif-ah text-[30px] text-[#2f2f2f] mb-3">See you there, {done.full_name.split(' ')[0]}!</h3>
        <p className="font-sans-ah text-[15px] text-[#666] font-300">
          We've reserved <strong>{done.tickets} ticket{done.tickets > 1 ? 's' : ''}</strong> for {eventTitle}. A confirmation has been sent to {done.email}.
        </p>
        <PaymentNote done={done} />
        <div className="flex flex-wrap justify-center gap-3 mt-7">
          <a href={done.google_calendar_url} target="_blank" rel="noopener noreferrer" data-testid="rsvp-add-google" className={calBtn}><CalendarPlus size={15} /> GOOGLE CALENDAR</a>
          <a href={`${API}/events/${done.event_id}/calendar.ics`} data-testid="rsvp-add-apple" className={calBtn}><CalendarPlus size={15} /> APPLE / OUTLOOK</a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="rsvp-form">
      <div className="flex items-center gap-3 mb-2">
        <Ticket size={22} className="text-[#4a9cd4]" />
        <h3 className="font-serif-ah text-[28px] text-[#2f2f2f]">RSVP for this event</h3>
      </div>
      <div>
        <label htmlFor="rsvp-name" className={labelCls}>Full Name *</label>
        <input id="rsvp-name" data-testid="rsvp-name-input" autoComplete="name" className={inputCls} value={form.full_name} onChange={set('full_name')} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rsvp-email" className={labelCls}>Email *</label>
          <input id="rsvp-email" type="email" data-testid="rsvp-email-input" autoComplete="email" className={inputCls} value={form.email} onChange={set('email')} />
        </div>
        <div>
          <label htmlFor="rsvp-phone" className={labelCls}>Phone Number *</label>
          <input id="rsvp-phone" type="tel" data-testid="rsvp-phone-input" autoComplete="tel" className={inputCls} value={form.phone} onChange={set('phone')} />
        </div>
      </div>
      {paidMode ? <TicketPicker event={event} value={paid} onChange={setPaid} guestsSlot={<GuestFields guests={guests} onChange={setGuestList} />} /> : (
        <div>
          <p className={labelCls}>Number of Tickets (including you)</p>
          <TicketStepper value={form.tickets} onChange={(tickets) => setForm((f) => ({ ...f, tickets }))} />
        </div>
      )}
      {!paidMode && <GuestFields guests={guests} onChange={setGuestList} />}
      <button type="submit" disabled={busy} data-testid="rsvp-submit-btn" className="btn-ah-solid w-full font-sans-ah text-[13px] py-4 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} {busy ? 'SENDING...' : 'CONFIRM RSVP'}
      </button>
    </form>
  );
}
